import { useState, useEffect } from 'react';
import { 
  Product, 
  CreateProductFormData
} from '../types/product';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { usePermissions } from './usePermissions';

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { hasAccess } = usePermissions();

  // Automatikus termékkód generálás
  const generateProductCode = (): string => {
    // MG prefixű kódok keresése a meglévő termékek között (kötőjel nélkül)
    const allMgCodes = products
      .map(product => product.code)
      .filter(code => code && code.startsWith('MG') && !code.includes('-'));
    
    const mgCodes = allMgCodes
      .map(code => {
        // MG után a számrész kinyerése
        const numberPart = code.substring(2);
        const num = parseInt(numberPart, 10);
        return isNaN(num) ? 0 : num;
      })
      .filter(num => num > 0) // Csak érvényes számokat tartunk meg
      .sort((a, b) => a - b);

    // Ha vannak MG számok, a legnagyobb utáni számot adjuk vissza
    let nextNumber = 1;
    if (mgCodes.length > 0) {
      const maxNumber = Math.max(...mgCodes);
      nextNumber = maxNumber + 1;
    }

    // 8 jegyű formátum biztosítása (00000001, 00000002, stb.)
    const paddedNumber = nextNumber.toString().padStart(8, '0');
    const generatedCode = `MG${paddedNumber}`;
    
    return generatedCode;
  };

  // Real-time listener
  useEffect(() => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('products')) {
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const loadedProducts = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          organizationProducts: doc.data().organizationProducts?.map((op: any) => ({
            ...op,
            createdAt: op.createdAt?.toDate() || new Date(),
            updatedAt: op.updatedAt?.toDate() || new Date(),
            validFrom: op.validFrom?.toDate() || new Date(),
            validUntil: op.validUntil?.toDate() || new Date()
          })) || []
        })) as Product[];
        
        setProducts(loadedProducts);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading products:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [hasAccess]);

  const createProduct = async (data: CreateProductFormData): Promise<string> => {
    try {
      const now = new Date();
      
      console.log('useProducts createProduct hívva:', {
        images: data.images?.length || 0,
        hiddenImages: data.hiddenImages?.length || 0
      });
      
      // Biztosítsuk, hogy a thumbnailUrl egyezzen a főképpel
      let thumbnailUrl = data.thumbnailUrl;
      if (data.images && data.images.length > 0) {
        // Megkeressük a főképet
        const mainImage = data.images.find(img => img.isMain);
        if (mainImage) {
          // Felülírjuk a thumbnailUrl-t a főkép URL-jével
          thumbnailUrl = mainImage.url;
        } else {
          // Ha nincs főkép, az első képet tesszük meg főképnek
          data.images[0].isMain = true;
          thumbnailUrl = data.images[0].url;
        }
      }
      
      const newProduct = {
        code: data.code,
        name: data.name,
        description: data.description,
        type: data.type,
        unit: data.unit,
        isActive: data.isActive,
        organizationProducts: data.organizations.map(org => ({
          ...org,
          productId: '',
          isActive: true,
          discounts: org.discounts || [],
          createdAt: now,
          updatedAt: now,
          validFrom: org.validFrom || now,
          validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
        })),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(data.category && { category: data.category }),
        ...(data.barcode && { barcode: data.barcode }),
        ...(data.netPrice && { netPrice: data.netPrice }),
        ...(data.grossPrice && { grossPrice: data.grossPrice }),
        ...(data.taxRate && { taxRate: data.taxRate }),
        ...(data.reverseTaxation !== undefined && { reverseTaxation: data.reverseTaxation }),
        ...(data.webStore !== undefined && { webStore: data.webStore }),
        ...(thumbnailUrl && { thumbnailUrl }),
        ...(data.images && data.images.length > 0 && { images: data.images }),
        ...(data.hiddenImages && data.hiddenImages.length > 0 && { hiddenImages: data.hiddenImages })
      };

      console.log('Firebase-be mentendő termék:', {
        ...newProduct,
        images: newProduct.images?.length || 0,
        hiddenImages: newProduct.hiddenImages?.length || 0
      });

      const docRef = await addDoc(collection(db, 'products'), newProduct);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  };

  const updateProduct = async (id: string, data: Partial<CreateProductFormData>): Promise<void> => {
    try {
      const docRef = doc(db, 'products', id);
      const now = new Date();
      
      console.log('useProducts updateProduct hívva:', {
        images: data.images?.length || 0,
        hiddenImages: data.hiddenImages?.length || 0
      });
      
      // Biztosítsuk, hogy a thumbnailUrl egyezzen a főképpel
      let thumbnailUrl = data.thumbnailUrl;
      if (data.images && data.images.length > 0) {
        // Megkeressük a főképet
        const mainImage = data.images.find(img => img.isMain);
        if (mainImage) {
          // Felülírjuk a thumbnailUrl-t a főkép URL-jével
          thumbnailUrl = mainImage.url;
        } else {
          // Ha nincs főkép, az első képet tesszük meg főképnek
          data.images[0].isMain = true;
          thumbnailUrl = data.images[0].url;
        }
      }
      
      const updateData = {
        ...(data.code && { code: data.code }),
        ...(data.name && { name: data.name }),
        ...(data.description && { description: data.description }),
        ...(data.type && { type: data.type }),
        ...(data.unit && { unit: data.unit }),
        ...(data.isActive !== undefined && { isActive: data.isActive }),
        ...(data.organizations && {
          organizationProducts: data.organizations.map(org => ({
            ...org,
            productId: id,
            isActive: true,
            discounts: org.discounts || [],
            createdAt: now,
            updatedAt: now,
            validFrom: org.validFrom || now,
            validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
          }))
        }),
        ...(data.category && { category: data.category }),
        ...(data.barcode && { barcode: data.barcode }),
        ...(data.netPrice && { netPrice: data.netPrice }),
        ...(data.grossPrice && { grossPrice: data.grossPrice }),
        ...(data.taxRate && { taxRate: data.taxRate }),
        ...(data.reverseTaxation !== undefined && { reverseTaxation: data.reverseTaxation }),
        ...(data.webStore !== undefined && { webStore: data.webStore }),
        ...(thumbnailUrl && { thumbnailUrl }),
        ...(data.images && { images: data.images }),
        ...(data.hiddenImages && { hiddenImages: data.hiddenImages }),
        updatedAt: serverTimestamp()
      };

      console.log('Firebase-ben frissítendő adatok:', {
        ...updateData,
        images: updateData.images?.length || 0,
        hiddenImages: updateData.hiddenImages?.length || 0
      });

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  // Termékkód elérhetőségének ellenőrzése
  const checkCodeAvailability = async (code: string, excludeId?: string): Promise<{ isAvailable: boolean; existingProductName?: string }> => {
    if (!code || code.trim() === '') {
      return { isAvailable: false };
    }

    // Normalizált kód (eltávolítjuk a felesleges szóközöket)
    const normalizedCode = code.trim();
    
    // Keresés a meglévő termékek között
    const existingProduct = products.find(product => 
      product.code === normalizedCode && 
      (excludeId ? product.id !== excludeId : true)
    );

    if (existingProduct) {
      return { 
        isAvailable: false, 
        existingProductName: existingProduct.name 
      };
    }

    return { isAvailable: true };
  };

  return {
    products,
    loading,
    error,
    createProduct,
    updateProduct,
    deleteProduct,
    generateProductCode,
    checkCodeAvailability
  };
}; 